import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Mic, Volume2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BAR_COUNT = 32;

/* ─── LED VU Meter ─── */
function VuMeter({ analyser, isPlaying }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const peaksRef = useRef(new Float32Array(BAR_COUNT));

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const barW = Math.floor(w / BAR_COUNT) - 1;
    const gap = 1;

    if (analyser && isPlaying) {
      const bufLen = analyser.frequencyBinCount;
      const data = new Uint8Array(bufLen);
      analyser.getByteFrequencyData(data);

      const step = Math.floor(bufLen / BAR_COUNT);
      for (let i = 0; i < BAR_COUNT; i++) {
        let sum = 0;
        for (let j = 0; j < step; j++) sum += data[i * step + j];
        const val = sum / step / 255;

        // Smooth peak decay
        if (val > peaksRef.current[i]) peaksRef.current[i] = val;
        else peaksRef.current[i] *= 0.95;

        const barH = Math.max(2, val * h);
        const peakH = peaksRef.current[i] * h;
        const x = i * (barW + gap);

        // LED segments
        const segH = 3;
        const segGap = 1;
        const totalSegs = Math.floor(h / (segH + segGap));
        const litSegs = Math.floor((barH / h) * totalSegs);
        const peakSeg = Math.floor((peakH / h) * totalSegs);

        for (let s = 0; s < totalSegs; s++) {
          const y = h - (s + 1) * (segH + segGap);
          const ratio = s / totalSegs;

          if (s < litSegs) {
            // Color gradient: dark red → bright red → white at peak
            if (ratio < 0.5) ctx.fillStyle = "#8B0000";
            else if (ratio < 0.75) ctx.fillStyle = "#E60000";
            else ctx.fillStyle = "#FF4444";
          } else if (s === peakSeg && peakSeg > 0) {
            ctx.fillStyle = "#FF6666";
          } else {
            ctx.fillStyle = "rgba(255,255,255,0.04)";
          }
          ctx.fillRect(x, y, barW, segH);
        }
      }
    } else {
      // Idle state: dim bars
      const segH = 3;
      const segGap = 1;
      const totalSegs = Math.floor(h / (segH + segGap));
      for (let i = 0; i < BAR_COUNT; i++) {
        peaksRef.current[i] = 0;
        const x = i * (barW + gap);
        for (let s = 0; s < totalSegs; s++) {
          const y = h - (s + 1) * (segH + segGap);
          ctx.fillStyle = "rgba(255,255,255,0.04)";
          ctx.fillRect(x, y, barW, segH);
        }
      }
    }

    animRef.current = requestAnimationFrame(draw);
  }, [analyser, isPlaying]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [draw]);

  // Resize canvas to container
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  return (
    <div className="w-full h-10 mt-3" data-testid="vu-meter">
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
}

/* ─── Track Player ─── */
function TrackPlayer({ track, index }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const analyserRef = useRef(null);
  const ctxRef = useRef(null);
  const sourceRef = useRef(null);

  const ensureAnalyser = () => {
    if (analyserRef.current) return analyserRef.current;
    const audio = audioRef.current;
    if (!audio) return null;
    const ctx = ctxRef.current || new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;
    if (!sourceRef.current) {
      sourceRef.current = ctx.createMediaElementSource(audio);
    }
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    sourceRef.current.connect(analyser);
    analyser.connect(ctx.destination);
    analyserRef.current = analyser;
    return analyser;
  };

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      ensureAnalyser();
      if (ctxRef.current?.state === "suspended") await ctxRef.current.resume();
      audioRef.current.play().catch(e => console.error("Play error:", e));
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    return () => {
      if (ctxRef.current && ctxRef.current.state !== "closed") {
        ctxRef.current.close().catch(() => {});
      }
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="p-5 border border-white/5 hover:border-[#E60000]/30 bg-[#121212]/50 transition-all duration-500 group"
      data-testid={`voiceover-track-${index}`}
    >
      <div className="flex items-start gap-4">
        <button
          onClick={togglePlay}
          className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
            isPlaying
              ? "border-[#E60000] bg-[#E60000]/20 shadow-[0_0_15px_rgba(230,0,0,0.3)]"
              : "border-white/15 hover:border-[#E60000] hover:bg-[#E60000]/10"
          }`}
          data-testid={`voiceover-play-${index}`}
        >
          {isPlaying ? <Pause className="w-4 h-4 text-[#E60000]" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-sm font-bold uppercase text-white tracking-tight mb-0.5">{track.title}</h3>
          <p className="font-body text-xs text-[#A3A3A3]">{track.description}</p>
          <VuMeter analyser={analyserRef.current} isPlaying={isPlaying} />
        </div>
      </div>
      <audio ref={audioRef} onEnded={() => setIsPlaying(false)} preload="none" crossOrigin="anonymous">
        {track.audio_url && (
          <source
            src={track.audio_url.startsWith("/api") ? `${API.replace("/api", "")}${track.audio_url}` : track.audio_url}
            type="audio/mpeg"
          />
        )}
      </audio>
    </motion.div>
  );
}

/* ─── VoiceOver Section ─── */
export default function VoiceOver() {
  const { t } = useLang();
  const [tracks, setTracks] = useState([]);

  useEffect(() => {
    axios.get(`${API}/content/voiceover_tracks`).then(r => setTracks(r.data)).catch(e => console.error("Voiceover fetch error:", e));
  }, []);

  return (
    <section id="voiceover" className="relative py-16 md:py-32 bg-[#0a0a0a] overflow-hidden" data-testid="voiceover-section">
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#E60000]/5 rounded-full blur-[200px]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-[#E60000]" />
              <span className="text-[#E60000] text-sm uppercase tracking-[0.3em] font-medium font-body">{t.voiceover.section_tag}</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold uppercase text-white mb-3" data-testid="voiceover-title">
              {t.voiceover.title} <span className="text-[#E60000]">{t.voiceover.title_accent}</span>
            </h2>
            <p className="font-heading text-lg text-[#E60000] font-semibold mb-4">{t.voiceover.subtitle_text}</p>
            <p className="font-body text-[#A3A3A3] leading-relaxed mb-8">{t.voiceover.description}</p>
            <ul className="space-y-3 mb-8">
              {t.voiceover.services.map((s) => (
                <li key={s} className="flex items-start gap-3">
                  <Mic className="w-4 h-4 text-[#E60000] mt-0.5 shrink-0" />
                  <span className="font-body text-sm text-white/70">{s}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => document.querySelector("#soumission")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-[#E60000] hover:bg-[#FF3333] text-white text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 transition-all duration-300 hover:shadow-lg hover:shadow-red-900/40"
              data-testid="voiceover-cta"
            >
              {t.voiceover.cta}
            </button>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }} className="space-y-3">
            <div className="flex items-center gap-3 mb-4">
              <Volume2 className="w-5 h-5 text-[#E60000]" />
              <h3 className="font-heading text-sm font-bold uppercase text-white tracking-tight">{t.voiceover.examples_title}</h3>
            </div>
            {tracks.length > 0
              ? tracks.map((track, i) => <TrackPlayer key={track.id || i} track={track} index={i} />)
              : [...Array(4)].map((_, i) => (
                  <div key={`skeleton-${i}`} className="p-6 border border-white/5 bg-[#121212]/50 animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-white/5" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-white/5 rounded w-1/3" />
                        <div className="h-2 bg-white/5 rounded w-2/3" />
                      </div>
                    </div>
                  </div>
                ))
            }
            <p className="font-body text-xs text-white/20 text-center mt-4">{t.voiceover.add_mp3}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
